import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherX } from "@subframe/core";
import axios from "axios";
import { getAuth } from "firebase/auth";
import { useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { clearDeviceLink } from "@/lib/device/device-link";
import { clearAllOfflineData } from "@/lib/offline/db";
import { type StalledCounts, syncService } from "@/lib/offline/syncService";

import { classifyFlush, type FlushResult } from "./classify-flush";
import { loginForDeviceAuth, resetDevice } from "./device-setup-api";

const INVALID_CREDENTIALS = "Incorrect email or password. Please try again.";
const RESET_OFFLINE = "Device reset requires a network connection.";
const UNSYNCED_BLOCKED =
  "Unsynced records can't be saved right now — reconnect and try again.";
const GENERIC_ERROR = "Something went wrong. Please try again.";

// NAU-226 Decision 6: a reset must never silently discard unsynced records — it
// flushes the queue first and refuses if anything is left after this timeout.
const FLUSH_TIMEOUT_MS = 10_000;

type Phase = null | "authenticating" | "syncing" | "resetting";

interface ResetDeviceDialogProps {
  deviceUuid: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called once the device link + caches are cleared (returns to setup). */
  onReset: () => void;
}

/**
 * Attempt to drain the offline sync queue before an unlink-and-wipe.
 *
 * Nothing may be discarded before this count is taken: discarding first is what
 * lets the gate pass, and the work behind those items is then wiped with no
 * count and no warning — the exact outcome the unsynced-work guarantee exists
 * to prevent.
 *
 * The two cases are therefore kept apart. Items that may still land block the
 * reset outright. Items that cannot land FROM HERE cannot block it
 * forever — that would lock a tablet out of re-provisioning — but they are
 * reported by count so a manager decides, rather than discovering afterwards.
 *
 * "From here" is load-bearing: this dialog runs on the pre-auth PIN screen,
 * where the manager is verified over HTTP without being signed into Firebase.
 * Sync therefore cannot run at all, so a parked item counts as stalled here
 * even though a later sign-in elsewhere might have landed it.
 */
async function flushSyncQueue(): Promise<FlushResult> {
  const { pendingCount } = await syncService.getStatus();
  if (pendingCount === 0) return { ok: true };

  const timeout = new Promise<void>((resolve) =>
    setTimeout(resolve, FLUSH_TIMEOUT_MS),
  );
  await Promise.race([syncService.immediateSync(), timeout]);

  const { pendingCount: after } = await syncService.getStatus();

  // Checked, not assumed. This dialog normally runs with no Firebase user, so
  // sync cannot run and parked items are stalled — but a session rejected by
  // the API clears its cached session without signing out of Firebase, and in
  // that state the same screen can drain after all. Treating parked items as
  // unsendable there would delete work that a sign-in could still have sent.
  const canSignInDrain = getAuth().currentUser !== null;

  return classifyFlush({
    pendingCount: after,
    stalled: await syncService.countStalled({ canSignInDrain }),
  });
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

export function ResetDeviceDialog({
  deviceUuid,
  open,
  onOpenChange,
  onReset,
}: ResetDeviceDialogProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phase, setPhase] = useState<Phase>(null);
  const [error, setError] = useState<string | null>(null);
  // Set once a flush proves nothing left can be sent from here. Showing what
  // will be lost and requiring a second press is the acknowledgement —
  // resetting deletes these, and nobody should learn that after the fact.
  const [stalled, setStalled] = useState<StalledCounts | null>(null);
  const unsendableCount = stalled ? stalled.rejected + stalled.parked : null;

  const isBusy = phase !== null;

  function close() {
    if (isBusy) return;
    setEmail("");
    setPassword("");
    setError(null);
    setStalled(null);
    onOpenChange(false);
  }

  async function handleReset() {
    setError(null);

    if (!navigator.onLine) {
      setError(RESET_OFFLINE);
      return;
    }

    try {
      setPhase("authenticating");
      const idToken = await loginForDeviceAuth(email.trim(), password);

      setPhase("syncing");
      const flush = await flushSyncQueue();
      if (!flush.ok) {
        if (flush.reason === "retryable") {
          // Drop any earlier warning: the queue has changed under us and those
          // records are no longer the reason the reset is stopping.
          setStalled(null);
          setError(UNSYNCED_BLOCKED);
          setPhase(null);
          return;
        }

        // Unsendable records. First press reports them and stops; a second
        // press is the manager accepting the loss. Re-prompt if the number
        // moved between presses, so the figure they accept is the figure that
        // is actually deleted.
        if (unsendableCount !== flush.count) {
          setStalled(flush.stalled);
          setPhase(null);
          return;
        }
      }

      setPhase("resetting");
      await resetDevice(idToken, deviceUuid);

      // The device is now unlinked server-side. Guarantee the local teardown so we
      // never strand a device that's linked locally but reset on the server: clear
      // the rest of the caches best-effort, then ALWAYS remove the link itself
      // (clearAllOfflineData deliberately preserves it — Decision 5).
      //
      // keepUnsyncedWork: false because this is the explicit unlink-and-wipe
      // path — the queue was flushed above, and the tablet may be re-provisioned
      // to a different nursery, so nothing captured here may survive. The
      // logout paths keep their work; this one must not (NAU-399 review).
      try {
        await clearAllOfflineData({ keepUnsyncedWork: false });
      } catch (cleanupError) {
        console.error(
          "Device reset: cache teardown failed; clearing link anyway",
          cleanupError,
        );
      }
      await clearDeviceLink();

      toast.success("Device reset. Set it up again to continue.");
      onReset();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError(INVALID_CREDENTIALS);
      } else {
        setError(GENERIC_ERROR);
      }
      setPhase(null);
    }
  }

  const buttonLabel =
    phase === "authenticating"
      ? "Authenticating…"
      : phase === "syncing"
        ? "Syncing…"
        : phase === "resetting"
          ? "Resetting…"
          : unsendableCount !== null
            ? `Delete ${unsendableCount} record${unsendableCount === 1 ? "" : "s"} and reset`
            : "Reset device";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
    >
      <DialogContent showCloseButton={false} className="max-w-md p-0">
        <DialogTitle className="sr-only">Reset device</DialogTitle>

        <div className="flex items-start justify-between border-b border-solid border-secondary-beige-200 px-6 py-4">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Reset device
            </span>
            <span className="text-body font-body text-subtext-color">
              A manager must confirm. This unlinks the device and removes its
              cached data.
            </span>
          </div>
          <IconButton
            variant="neutral-tertiary"
            size="medium"
            icon={<FeatherX />}
            disabled={isBusy}
            onClick={close}
          />
        </div>

        <form
          className="flex flex-col gap-6 px-6 py-6"
          onSubmit={(e) => {
            e.preventDefault();
            void handleReset();
          }}
        >
          <TextField className="h-auto w-full" size="large" label="Email">
            <TextField.Input
              type="email"
              placeholder="manager@nursery.com"
              value={email}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              disabled={isBusy}
              onChange={(e) => setEmail(e.target.value)}
            />
          </TextField>

          <TextField className="h-auto w-full" size="large" label="Password">
            <TextField.Input
              type="password"
              placeholder="Enter password"
              value={password}
              autoComplete="current-password"
              disabled={isBusy}
              onChange={(e) => setPassword(e.target.value)}
            />
          </TextField>

          {stalled !== null && (
            <span className="flex flex-col gap-1 text-body font-body text-error-700">
              <span>
                Resetting will permanently delete{" "}
                {plural(stalled.rejected + stalled.parked, "record")} still held
                on this device:
              </span>
              {stalled.rejected > 0 && (
                <span>
                  &bull; {plural(stalled.rejected, "record")} the server
                  rejected &mdash; these cannot be sent by anyone.
                </span>
              )}
              {stalled.parked > 0 && (
                <span>
                  &bull; {plural(stalled.parked, "record")} waiting for an
                  authorised user to sign in. These{" "}
                  <strong>could still be sent</strong> if that person signs in
                  on this device first.
                </span>
              )}
            </span>
          )}

          {error && (
            <span className="text-body font-body text-error-700">{error}</span>
          )}

          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="neutral-primary"
              size="large"
              disabled={isBusy}
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive-primary"
              size="large"
              disabled={isBusy || !email || !password}
              loading={isBusy}
            >
              {buttonLabel}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
